import {assertAndContinue, assert} from 'Asserts';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

import {TabbedUISchema} from './step-config-settings/StepConfigUISchemas';
// Use Symbols so that there's no possibility that a variable name and these keys conflict in a
// `ShapeUISchema` object.
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const FIELD_ORDER_UI_SCHEMA_KEY = Symbol('FieldOrderUISchemaKey');
/**
 * @see documenatation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const HIDDEN_FIELDS_KEY = Symbol('HiddenFieldsSchemaKey');
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const ON_CHANGE_CONFIG_MIDDLEWARE_KEY = Symbol('OnChangeConfigMiddlewareKey');

/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const CUSTOM_COMPONENT_KEY = Symbol('CustomComponent');

/**
 * Order the tabs in the tabbed container specified by the `TabbedUISchema`.
 */
export const TAB_ORDER_UI_SCHEMA_KEY = Symbol('TabOrderUISchemaKey');

/**
 * The name of the section to display in the tabbed container specified by the `TabbedUISchema`.
 */
export const SECTION_NAME_UI_SCHEMA_KEY = Symbol('SectionNameUISchemaKey');

/**
 * Whether to show a divider line between sections in the tabbed container specified by the
 * `ShapeUISchemaV2`.
 */
export const SHOW_DIVIDER_LINE_KEY = Symbol('ShowDividerLineUISchemaKey');

export const CONDITIONAL_UI_SCHEMA_KEY = Symbol('ConditionalUISchemaKey');

/**
 * Allows info text for section names
 * `ShapeUISchemaV2`.
 */
export const INFO_TEXT_UI_SCHEMA_KEY = Symbol('InfoTextUISchemaKey');

interface FieldUISchemaWithStandardComponent {
  displayName?: string;
  infoText?: string;
  fieldSettings?: StepConfigUISchema;
}

export enum StepConfigEditorOverride {
  CHECKBOX = 'Checkbox',
}

export const STEP_CONFIG_EDITOR_TO_VALID_KIND: {
  [key in StepConfigEditorOverride]: Array<PipelineConfigJsonSpec['kind']>;
} = {
  [StepConfigEditorOverride.CHECKBOX]: ['boolean'],
} as const;

interface EnumFieldSettings {
  displayNames?: Record<string, string>;
  defaultValue?: string | number;
}

interface TaggedUnionFieldSettings {
  tagKeyDisplayNames?: Record<string, string>;
}

interface CheckboxFieldSettings {
  checkboxLabel: string;
}

interface ColumnReferenceFieldSettings {
  noColumnSelectedText?: string;
  allowSelectNull?: boolean;
}

interface ColumnValueFieldSettings {
  noValueSelectedText?: string;
}

interface TableReferenceFieldSettings {
  disabledColumn?: Array<string>;
  hiddenColumn?: Array<string>;
}

export type FieldSettingsV2 =
  | EnumFieldSettings
  | TaggedUnionFieldSettings
  | CheckboxFieldSettings
  | ColumnReferenceFieldSettings
  | ColumnValueFieldSettings
  | TableReferenceFieldSettings;

export interface DisableFunctionParams {
  config: Record<string, JSONValue>;
  columns: Array<DataFrameColumnDef>;
}
export type DisableFunction = (params: DisableFunctionParams) => boolean;

/** Schema of a scalar field.
 *
 * Keep in sync with `FieldUISchemaWithStandardComponentV2` (benchling/pipelines/step_type_library/manifests/types/ui_schema.yaml) on the back-end
 */
export interface FieldUISchemaWithStandardComponentV2 {
  displayName?: string;
  editorOverride?: StepConfigEditorOverride;
  infoText?: string;
  configShapePath: Array<string>;
  fieldSettings?: FieldSettingsV2;
  disableFn?: DisableFunction;
}

export function assertTypeValidForEditorOverride(
  editorOverride: StepConfigEditorOverride,
  configShape: PipelineConfigJsonSpec
): void {
  assertAndContinue(
    STEP_CONFIG_EDITOR_TO_VALID_KIND[editorOverride].includes(configShape.kind),
    'Editor override does not match config shape kind'
  );
}

interface FieldUISchemaWithCustomComponent {
  displayName?: string;
  infoText?: string;
  disableFn?: DisableFunction;
  [CUSTOM_COMPONENT_KEY]: (props: {
    config: JSONValue;
    onChangeConfig: (newConfig: JSONValue) => void;
    configShape: PipelineConfigJsonSpec;
    disabled?: boolean;
  }) => JSX.Element;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    inputDataFrames: Record<string, Array<DataFrameSpec>>
  ) => JSONValue;
}

export interface CustomComponentEditorProps {
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: Array<string>, newValue: JSONValue) => void;
  disabled?: boolean;
  configShape: PipelineConfigJsonSpec;
}
interface FieldUISchemaWithCustomComponentV2 {
  displayName?: string;
  infoText?: string;
  [CUSTOM_COMPONENT_KEY]: (props: CustomComponentEditorProps) => JSX.Element;
  disableFn?: DisableFunction;
}

/** @package */
export interface ShapeUISchema {
  [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<string>;
  [HIDDEN_FIELDS_KEY]?: ReadonlyArray<string>;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    inputDataFrames: Record<string, Array<DataFrameSpec>>
  ) => JSONValue;
  [key: string]: FieldUISchemaWithStandardComponent | FieldUISchemaWithCustomComponent;
}
interface ShapeUISchemaV2SharedFields {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>;
  [SECTION_NAME_UI_SCHEMA_KEY]?: string;
  [SHOW_DIVIDER_LINE_KEY]?: boolean;
  [CONDITIONAL_UI_SCHEMA_KEY]?: {
    tagValue: string;
    configShapePath: Array<string>;
  };
  [INFO_TEXT_UI_SCHEMA_KEY]?: string;
}

/** @package */
export type ShapeUISchemaV2FieldComponentValues = ShapeUISchemaV2SharedFields & {
  [key: string]: FieldUISchemaWithStandardComponentV2 | FieldUISchemaWithCustomComponentV2;
};

/** @package */
export type ShapeUISchemaV2NestedShapeValues = ShapeUISchemaV2SharedFields & {
  [key: string]: ShapeUISchemaV2;
};

export type ShapeUISchemaV2 = ShapeUISchemaV2FieldComponentValues | ShapeUISchemaV2NestedShapeValues;

/** @package */
export interface EnumUISchema {
  displayNames?: Record<string | number, string>;
  infoText?: string;
  defaultValue?: string | number;
  placeholder?: string;
}

/** @package */
export interface TaggedUnionUISchema {
  placeholder?: string;
  infoText?: string;
  tagKeyDisplayNames?: Record<string, string>;
  choiceSettings?: ShapeUISchema;
  [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<string>;
  [HIDDEN_FIELDS_KEY]?: ReadonlyArray<string>;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    inputDataFrames: Record<string, Array<DataFrameSpec>>
  ) => JSONValue;
}

/** @package */
export type StepConfigUISchema = ShapeUISchema | TaggedUnionUISchema | EnumUISchema | undefined;

export type StepConfigUISchemaV2 =
  | ShapeUISchemaV2
  | FieldUISchemaWithStandardComponentV2
  | FieldUISchemaWithCustomComponentV2;

export type StepConfigUISchemaWithTabsV2 = TabbedUISchema | StepConfigUISchemaV2;

export function isShapeUISchemaV2(schema: StepConfigUISchemaV2): schema is ShapeUISchemaV2 {
  return schema.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaV2NestedShapeValues(
  schema: ShapeUISchemaV2
): schema is ShapeUISchemaV2NestedShapeValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaV2FieldComponentValue(
  schema: ShapeUISchemaV2
): schema is ShapeUISchemaV2FieldComponentValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return !firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isFieldUISchemaWithStandardComponentV2(
  schema: StepConfigUISchemaV2
): schema is FieldUISchemaWithStandardComponentV2 {
  return schema != null && schema.hasOwnProperty('configShapePath');
}

export function isTabbedUISchema(schema: StepConfigUISchemaWithTabsV2): schema is TabbedUISchema {
  return schema.hasOwnProperty(TAB_ORDER_UI_SCHEMA_KEY);
}

function isFieldUISchemaWithStandardComponent(
  field: ShapeUISchema[keyof ShapeUISchema] | ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaWithStandardComponent {
  return field != null && field.hasOwnProperty('fieldSettings');
}

export function getFieldSettingsIfPresent(
  schema: ShapeUISchema | undefined,
  fieldName: string
): StepConfigUISchema | undefined {
  const field = schema?.[fieldName];
  if (isFieldUISchemaWithStandardComponent(field)) {
    return field.fieldSettings;
  }
  return undefined;
}

export function getFieldSettingsIfPresentV2(
  schema: ShapeUISchemaV2 | undefined,
  fieldName: string
): StepConfigUISchemaV2 | undefined {
  // V2 Schemas have the fieldSettings doubly nested in the field object
  // under the same fieldName
  const field = schema?.[fieldName];
  if (field != null && field.hasOwnProperty(fieldName)) {
    const nestedField = (field as ShapeUISchemaV2)[fieldName];
    if (isFieldUISchemaWithStandardComponent(nestedField)) {
      return nestedField.fieldSettings as StepConfigUISchemaV2;
    }
  }
  return undefined;
}

export function isFieldUISchemaWithCustomComponent(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaWithCustomComponent {
  return field != null && field.hasOwnProperty(CUSTOM_COMPONENT_KEY);
}

export function isFieldUISchemaWithCustomComponentV2(
  field: ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaWithCustomComponentV2 {
  return field != null && field.hasOwnProperty(CUSTOM_COMPONENT_KEY);
}

export function isCheckboxFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is CheckboxFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('checkboxLabel');
}

export function isColumnReferenceFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is ColumnReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('noColumnSelectedText') || fieldSettings.hasOwnProperty('allowSelectNull'))
  );
}

export function isColumnValueFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is ColumnValueFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('noValueSelectedText');
}

export function isTableReferenceFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is TableReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('disabledColumn') || fieldSettings.hasOwnProperty('hiddenColumn'))
  );
}
